import { Form as BaseForm } from "@base-ui/react/form";
import clsx from "clsx";
import type { ComponentProps, FormEvent, ReactNode } from "react";
import styles from "./Form.module.css";

export interface FormProps extends Omit<ComponentProps<"form">, "className" | "style" | "onSubmit" | "children"> {
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  /**
   * Server field errors keyed by field `name` (ApiError.fieldErrors); fields with that name show
   * them and the first invalid field gets focus.
   */
  errors?: Record<string, string | string[]>;
  /** When to run the browser's constraint validation: on submit (default), or on blur. */
  validationMode?: "onSubmit" | "onBlur";
  children: ReactNode;
  className?: string;
}

/** A form (Base UI Form) laid out as a single column that stops at --field-max for its fields. */
export function Form({ onSubmit, errors, validationMode = "onSubmit", children, className, ...rest }: FormProps) {
  return (
    <BaseForm
      {...rest}
      errors={errors}
      validationMode={validationMode}
      className={clsx(styles.form, className)}
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(event);
      }}
    >
      {children}
    </BaseForm>
  );
}

/** A titled group of fields inside a form (a <fieldset> with a legend); fields flow in a grid. */
export function FormSection({
  title,
  description,
  children,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <fieldset className={clsx(styles.section, className)}>
      <legend className={styles.legend}>{title}</legend>
      {description ? <p className={styles.description}>{description}</p> : null}
      {children ? <div className={styles.fields}>{children}</div> : null}
    </fieldset>
  );
}

/** The row of form buttons (primary action last, at the end of the row). */
export function FormActions({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={clsx(styles.actions, className)}>{children}</div>;
}
